import React, { useEffect, useMemo } from 'react';
import { ArrowLeft, ArrowRight } from '@phosphor-icons/react';

/**
 * Book-style prev/next navigator — Feb 2026 (iter85).
 *
 * Lets a user page through a list of records one detail page at a time
 * (Products already had this — now shipped everywhere: Leads, Quotations,
 * Customers). Rather than have each page reimplement the state, the parent
 * hands us the ordered list and a currentId; we render the pill and wire
 * up ArrowLeft / ArrowRight keyboard shortcuts.
 *
 * Props:
 *   items       – ordered array like [{ id, label }]. Matches whatever
 *                 order the list page shows (usually created_at DESC).
 *   currentId   – the currently-open record's id.
 *   onNavigate  – (targetId) => void. Called on click or keyboard.
 *   testIdPrefix – prefixes the data-testids ("lead", "quotation", …).
 *   disabled    – suppress keyboard shortcuts while editing / typing.
 *   className   – extra classes on the outer flex container.
 */
export default function PrevNextNav({
  items = [],
  currentId,
  onNavigate,
  testIdPrefix = 'record',
  disabled = false,
  className = '',
}) {
  const currentIndex = useMemo(
    () => items.findIndex((it) => it && it.id === currentId),
    [items, currentId],
  );
  const prev = currentIndex > 0 ? items[currentIndex - 1] : null;
  const next = currentIndex >= 0 && currentIndex < items.length - 1
    ? items[currentIndex + 1]
    : null;

  useEffect(() => {
    if (disabled) return undefined;
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || '';
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'ArrowLeft' && prev) {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'instant' });
        onNavigate(prev.id);
      } else if (e.key === 'ArrowRight' && next) {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'instant' });
        onNavigate(next.id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prev?.id, next?.id, disabled, onNavigate]);

  if (!items.length || currentIndex < 0 || items.length < 2) return null;

  const go = (target) => {
    if (!target) return;
    window.scrollTo({ top: 0, behavior: 'instant' });
    onNavigate(target.id);
  };

  return (
    <div
      className={`inline-flex items-center gap-2 ${className}`}
      data-testid={`${testIdPrefix}-nav-bar`}
    >
      <button
        type="button"
        onClick={() => go(prev)}
        disabled={!prev}
        className={`inline-flex items-center gap-1 px-3 py-1.5 border rounded-lg text-sm font-medium transition ${
          prev
            ? 'border-[#00a69c] text-[#00a69c] hover:bg-[#E2F3F0]'
            : 'border-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed'
        }`}
        data-testid={`${testIdPrefix}-nav-prev-btn`}
        title={prev ? `Previous: ${prev.label || ''}` : 'This is the first record'}
      >
        <ArrowLeft size={16} /> Previous
      </button>
      <span
        className="text-xs text-[#6D8E87] font-medium tabular-nums px-1"
        data-testid={`${testIdPrefix}-nav-counter`}
      >
        {currentIndex + 1} / {items.length}
      </span>
      <button
        type="button"
        onClick={() => go(next)}
        disabled={!next}
        className={`inline-flex items-center gap-1 px-3 py-1.5 border rounded-lg text-sm font-medium transition ${
          next
            ? 'border-[#00a69c] text-[#00a69c] hover:bg-[#E2F3F0]'
            : 'border-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed'
        }`}
        data-testid={`${testIdPrefix}-nav-next-btn`}
        title={next ? `Next: ${next.label || ''}` : 'This is the last record'}
      >
        Next <ArrowRight size={16} />
      </button>
    </div>
  );
}
